// Дребните общи парчета на редизайнатите списъци и справки: KPI карти,
// сегменти с броячи, хапчета за статус и тип документ, търсачка, секция-карта.
// Всяко е малко, затова живеят заедно; по-големите (период, филтри, меню на
// реда) са в собствени файлове.
import type { ComponentType, ReactNode } from "react";
import { SearchOutlined } from "@ant-design/icons";
import { useInventoryShell } from "../shell/InventoryShellContext";
import { SIGNAL_BADGE_CLASS, type SignalTone } from "./signals";

// ─── KPI карти (auto-fit решетка, икон-чип по избор) ───────────────────────
export type KpiCard = {
  label: string;
  value: string;
  Icon?: ComponentType<{ className?: string }>;
  // Тонът на стойността: кехлибарен за водещата сума, червен за „Неплатени"
  tone?: "default" | "brand" | "danger";
};

const KPI_TONE_CLASS = {
  default: "text-ink",
  brand: "text-brand-deep-ink",
  danger: "text-danger-ink",
} as const;

export const KpiGrid = ({ cards }: { cards: KpiCard[] }) => (
  <div className="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-3">
    {cards.map((card) => (
      <div
        key={card.label}
        className="flex items-center gap-3.5 rounded-xl border border-shade-2 bg-panel px-[18px] py-4"
      >
        {card.Icon && (
          <span className="inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[9px] bg-brand-tint text-brand-deep-ink">
            <card.Icon className="text-[15px]" />
          </span>
        )}
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase leading-[normal] tracking-[0.1em] text-muted">
            {card.label}
          </div>
          <div
            className={`mt-1.5 truncate font-sheet text-[22px] font-semibold leading-[1.1] tabular-nums ${
              KPI_TONE_CLASS[card.tone ?? "default"]
            }`}
          >
            {card.value}
          </div>
        </div>
      </div>
    ))}
  </div>
);

// ─── Сегменти с броячи (Всички · Разнесени · Анулирани) ─────────────────────
export type SegmentOption<K extends string> = {
  key: K;
  label: string;
  count?: number;
};

export function SegmentedCounts<K extends string>({
  options,
  value,
  onChange,
  fill = false,
}: {
  options: SegmentOption<K>[];
  value: K;
  onChange: (key: K) => void;
  // Разпъва сегментите по цялата ширина (модали, тесни полета)
  fill?: boolean;
}) {
  return (
    <div
      role="radiogroup"
      className={`flex gap-0.5 rounded-[9px] border border-shade-3 bg-surface p-[3px] ${
        fill ? "w-full" : ""
      }`}
    >
      {options.map((option) => {
        const active = option.key === value;
        return (
          <button
            key={option.key}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(option.key)}
            className={`flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border-none px-3 py-1.5 text-[13px] leading-[normal] outline-none focus-visible:shadow-[0_0_0_2px_var(--color-brand)] ${
              fill ? "flex-1" : ""
            } ${
              active
                ? "bg-brand-tint font-semibold text-brand-deep-ink"
                : "bg-transparent font-normal text-body hover:bg-shade-1"
            }`}
          >
            {option.label}
            {option.count != null && (
              <span className="font-sheet text-xs opacity-80">{option.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ─── Хапче за статус ───────────────────────────────────────────────────────
export type PillTone = "ok" | "danger" | "neutral" | "brand" | "info" | "busy";

const PILL_TONE_CLASS: Record<PillTone, string> = {
  ok: "bg-ok-tint text-ok-deep",
  danger: "bg-danger-tint text-danger-ink",
  neutral: "bg-fill-2 text-body",
  brand: "bg-brand-tint text-brand-deep-ink",
  info: "bg-info-tint text-info-deep",
  busy: "bg-busy-tint text-busy",
};

const DOT_TONE_CLASS: Record<PillTone, string> = {
  ok: "bg-ok-solid",
  danger: "bg-danger",
  neutral: "bg-faint",
  brand: "bg-brand",
  info: "bg-info",
  busy: "bg-busy",
};

export const StatusPill = ({
  tone,
  children,
  dot = false,
}: {
  tone: PillTone;
  children: ReactNode;
  dot?: boolean;
}) => (
  <span
    className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-[3px] text-xs font-medium leading-[normal] ${PILL_TONE_CLASS[tone]}`}
  >
    {dot && <span className={`h-[7px] w-[7px] shrink-0 rounded-full ${DOT_TONE_CLASS[tone]}`} />}
    {children}
  </span>
);

// Точка за статус пред текст (списъкът с чакащи отчети, редовете)
export const StatusDot = ({ tone }: { tone: PillTone }) => (
  <span className={`h-[7px] w-[7px] shrink-0 rounded-full ${DOT_TONE_CLASS[tone]}`} />
);

// ─── Хапче със сигнал (тоновете — `signals.ts`) ────────────────────────────
// Хапче-значка със сигнал („Незавършена · Мария · 12 преброени")
export const SignalPill = ({ tone, children }: { tone: SignalTone; children: ReactNode }) => (
  <span
    className={`inline-flex min-w-0 max-w-full items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-[3px] text-xs font-medium leading-[normal] ${SIGNAL_BADGE_CLASS[tone]}`}
  >
    <span className="min-w-0 truncate">{children}</span>
  </span>
);

// ─── Тип документ (Фактура / Стокова) ──────────────────────────────────────
export type DocTypeTone = "invoice" | "note" | "fresh";

const DOC_TYPE_CLASS: Record<DocTypeTone, string> = {
  invoice: "bg-brand-tint text-brand-deep-ink",
  note: "bg-info-tint text-info-deep",
  fresh: "bg-fill-2 text-body",
};

export const DocTypeBadge = ({
  tone,
  children,
}: {
  tone: DocTypeTone;
  children: ReactNode;
}) => (
  <span
    className={`inline-flex shrink-0 items-center whitespace-nowrap rounded-[5px] px-[7px] py-0.5 text-[10.5px] font-bold uppercase leading-[normal] tracking-[0.06em] ${DOC_TYPE_CLASS[tone]}`}
  >
    {children}
  </span>
);

// ─── Търсачка на справка ───────────────────────────────────────────────────
// 16px на телефон — под 16px iOS Safari зуумва страницата при фокус.
export const SearchBox = ({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) => {
  const { isNarrow } = useInventoryShell();
  return (
    <label className="flex min-w-[200px] flex-[1_1_240px] items-center gap-2 rounded-[9px] border border-shade-3 bg-surface px-3 focus-within:border-brand">
      <SearchOutlined className="text-faint" />
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className={`min-w-0 flex-1 border-none bg-transparent py-[9px] text-ink outline-none placeholder:text-faint ${
          isNarrow ? "text-base" : "text-sm"
        }`}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          aria-label="×"
          className="cursor-pointer border-none bg-transparent p-0 text-base leading-none text-faint hover:text-ink"
        >
          ×
        </button>
      )}
    </label>
  );
};

// ─── Карта-секция (списък, таблица) ────────────────────────────────────────
export const SectionCard = ({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) => (
  // `shrink-0`: картите стоят в колона, която скролира (`flex-col overflow-y-auto`).
  // С `overflow-hidden` флекс елементът иначе се СВИВА до височината на екрана и
  // изрязва редовете си — колоната няма какво да скролира (справките до 29.09.2026).
  <div
    className={`shrink-0 overflow-hidden rounded-xl border border-shade-2 bg-panel ${className}`}
  >
    {children}
  </div>
);

// Заглавие на секция в начален екран („Незавършена доставка", „Въведени днес")
export const SectionTitle = ({
  title,
  hint,
}: {
  title: string;
  hint?: ReactNode;
}) => (
  <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
    <h2 className="m-0 text-base font-semibold leading-[1.2] text-ink">{title}</h2>
    {hint && <span className="text-[13px] leading-[normal] text-muted">{hint}</span>}
  </div>
);

// Празно състояние на цял екран („Всички отчети са разнесени")
export const EmptyScreen = ({
  Icon,
  tone = "ok",
  title,
  text,
  action,
}: {
  Icon: ComponentType<{ className?: string }>;
  tone?: "ok" | "brand";
  title: string;
  text?: string;
  action?: ReactNode;
}) => (
  <div className="flex flex-1 items-center justify-center p-7">
    <div className="flex max-w-[420px] flex-col items-center gap-2.5 text-center">
      <span
        className={`flex h-11 w-11 items-center justify-center rounded-full text-xl ${
          tone === "ok" ? "bg-ok-tint text-ok-deep" : "bg-brand-tint text-brand-deep-ink"
        }`}
      >
        <Icon />
      </span>
      <span className="text-[17px] font-semibold leading-[1.2] text-ink">{title}</span>
      {text && <span className="text-sm leading-[1.5] text-body">{text}</span>}
      {action && <div className="mt-1.5">{action}</div>}
    </div>
  </div>
);
